import {
  Box,
  Drawer,
  DrawerProps,
  ListItemIcon,
  MenuItem,
  Stack,
  styled,
  Typography,
  TypographyProps,
} from '@mui/material';
import { forwardRef } from 'react';

import { doNotForwardTheseProps } from '@/styles';

import { StyledFormControl } from '../Select/Select.styled';

export const GroupedSelectControl = styled(Stack)(({ theme }) => ({
  alignItems: 'stretch',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  width: '100%',
}));

export const GroupedSelectFieldLabel = styled(Typography, {
  shouldForwardProp: doNotForwardTheseProps('error'),
})<TypographyProps & { error?: boolean }>(({ theme, error }) => ({
  color: error ? theme.palette.error.main : theme.palette.text.secondary,
  display: 'block',
  fontSize: theme.typography.body2.fontSize,
  fontWeight: theme.typography.body2.fontWeight,
  width: '100%',
}));

export const GroupedSelectFormControl = styled(StyledFormControl)(
  ({ theme }) => ({
    '.MuiSelect-root': {
      height: 44,
    },

    '.MuiOutlinedInput-root': {
      backgroundColor: theme.palette.common.white,
      height: 'auto',
    },

    '.MuiSelect-root.multiSelectItem': {
      alignItems: 'center',
      height: 'auto',
    },

    '.MuiSelect-root.multiSelectItem .MuiSelect-select': {
      alignItems: 'center',
      display: 'flex',
      height: 'auto !important',
      minHeight: 'unset',
      whiteSpace: 'normal',
    },
  }),
);

export const GroupedSelectSelectedValue = styled(Typography)<TypographyProps>(
  ({ theme }) => ({
    display: 'block',
    lineHeight: theme.typography.body2.lineHeight,
    whiteSpace: 'normal',
    width: '100%',
    wordBreak: 'break-word',
  }),
);

export const GroupedSelectBottomSheet = styled(
  forwardRef<HTMLDivElement, DrawerProps>((props, ref) => (
    <Drawer ref={ref} anchor="bottom" {...props} />
  )),
)(({ theme }) => ({
  '&.MuiDrawer-modal': {
    zIndex: theme.zIndex.modal,

    '.MuiDrawer-paper': {
      borderTopLeftRadius: 24,
      borderTopRightRadius: 24,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      maxHeight: '85vh',
      width: '100%',
      zIndex: theme.zIndex.modal,
    },
  },
}));

export const BottomSheetHandle = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.neutral[400],
  borderRadius: 100,
  height: 4,
  margin: `${theme.spacing(1.5)} auto ${theme.spacing(2)}`,
  width: 40,
}));

export const BottomSheetHeader = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(1, 2, 2, 2),
}));

export const OptionsContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  paddingBottom: theme.spacing(4),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  width: '100%',
}));

export const GroupedSelectOptionItem = styled(MenuItem)(({ theme }) => ({
  alignItems: 'flex-start',
  borderRadius: theme.shape.borderRadius,
  display: 'flex',
  justifyContent: 'flex-start',
  minHeight: 48,
  padding: theme.spacing(1.5, 1.5),
  whiteSpace: 'normal',

  ':hover': {
    backgroundColor: theme.palette.neutral[100],
  },

  '.MuiListItemText-root': {
    flex: 1,
    margin: 0,
    minWidth: 0,
  },

  '&.Mui-selected': {
    backgroundColor: theme.palette.primaries[100],

    ':hover, :focus': {
      backgroundColor: theme.palette.primaries[100],
    },
  },
}));

export const GroupedSelectOptionCheckIcon = styled(ListItemIcon)(
  ({ theme }) => ({
    '&.MuiListItemIcon-root': {
      alignSelf: 'center',
      marginLeft: theme.spacing(1),
      minWidth: 0,
    },
  }),
);

export const GroupSubheading = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
}));

export const GroupSection = styled(Box)(({ theme }) => ({
  ':not(:first-of-type)': {
    paddingTop: theme.spacing(2),
  },
}));

export const GroupDivider = styled(Box)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.neutral[200]}`,
  marginBottom: theme.spacing(2),
  width: '100%',
}));

export const OtherOptionSection = styled(Box)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.neutral[200]}`,
  marginTop: theme.spacing(2),
  paddingTop: theme.spacing(1),
}));
